<template>
  <modal :id="modalId" ref="modal" @close="close" is-simplified-modal title="Re-Order Knowledge Base Categories">
    <template v-slot:body>
      <div>
        <!-- Title Container with colored background -->
        <div class="bg-primary-extra-lightest p-5">
          <div class="text-sm italic text-gray-700 mb-2">
            Drag and drop categories to reorder them. Changes will be visible to all users.
          </div>
          <div class="text-xs text-gray-500">
            Uncategorized will always appear at the bottom
          </div>
        </div>

        <!-- Content Container -->
        <div class="px-5 py-5 pb-4">
          <div class="mb-4">
            <h4 class="text-sm font-medium text-gray-700 mb-2">Category Order</h4>
            <p class="text-xs text-gray-500 mb-4">Drag categories to reorder them. The top category will appear first.</p>
          </div>

          <div class="space-y-2">
            <draggable
              v-model="orderedCategories"
              item-key="id"
              class="space-y-2"
              ghost-class="ghost"
              drag-class="drag"
              @change="handleDragChange"
            >
              <template #item="{ element: category }">
                <div class="tw-flex items-center p-3 bg-white border border-gray-200 rounded-md cursor-move hover:border-primary-community transition-colors">
                  <Icon name="menu" class="w-4 h-4 text-gray-400 mr-3 flex-shrink-0"/>
                  <div class="flex-1">
                    <div class="font-medium text-gray-900">{{ category.name }}</div>
                    <!--                    <div class="text-xs text-gray-500">ID: {{ category.id }}</div>-->
                  </div>
                  <div class="text-xs text-gray-400">#{{ orderedCategories.findIndex(c => c.id === category.id) + 1 }}</div>
                </div>
              </template>
            </draggable>
          </div>

          <!-- Uncategorized Category (Fixed at bottom) -->
          <div class="mt-4 pt-4 border-t border-gray-200">
            <div class="tw-flex items-center p-3 bg-gray-50 border border-gray-200 rounded-md opacity-60">
              <Icon name="lock" class="w-4 h-4 text-gray-400 mr-3 flex-shrink-0"/>
              <div class="flex-1">
                <div class="font-medium text-gray-700">Uncategorized</div>
                <div class="text-xs text-gray-500">Always appears at the bottom</div>
              </div>
              <div class="text-xs text-gray-400">#{{ orderedCategories.length + 1 }}</div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template v-slot:footer>
      <div class="w-full tw-flex gap-3">
        <ds-button
          variant="minimal"
          @click="close"
          label="Cancel"
          size="extra-small"
          class="flex-1"
        />
        <ds-button
          variant="rounded"
          @click="saveOrder"
          label="Save Order"
          size="extra-small"
          class="flex-1"
        />
      </div>
    </template>
  </modal>
</template>

<script>
  import Modal from '@/components/Modals/Modal.vue'
  import Icon from '@/components/Icon/Icon.vue'
  import draggable from 'vuedraggable'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import MODAL_IDS from '@/constants/modal-ids.js'

  export default {
    name: 'ReorderCategoriesModal',
    components: {
      Modal,
      Icon,
      draggable
    },
    data () {
      return {
        modalId: 'reorder-categories-modal',
        orderedCategories: []
      }
    },
    computed: {
      modalContext () {
        return this.$store.state.ui.modalContext
      }
    },
    mounted () {
      if (this.modalContext) {
        // Initialize with current categories and order
        this.orderedCategories = [...this.modalContext.categories]

        // If there's a custom order, apply it
        if (this.modalContext.currentOrder && this.modalContext.currentOrder.length > 0) {
          const categoryMap = new Map(this.orderedCategories.map(cat => [cat.id, cat]))
          const orderedCats = this.modalContext.currentOrder
            .map(id => categoryMap.get(id))
            .filter(Boolean)

          // Add any categories not in the current order
          const orderedIds = new Set(this.modalContext.currentOrder)
          const remainingCats = this.orderedCategories.filter(cat => !orderedIds.has(cat.id))

          this.orderedCategories = [...orderedCats, ...remainingCats]
        } else {
          // Sort alphabetically as default
          this.orderedCategories.sort((a, b) => a.name.localeCompare(b.name))
        }
      }
    },
    methods: {
      close () {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, null)
        this.$store.commit(UI_MUTATION_TYPES.HIDE_MODAL, MODAL_IDS.REORDER_CATEGORIES)
        this.$emit('close')
      },
      handleDragChange (event) {
        // Handle drag and drop events if needed
        console.log('Category order changed:', event)
      },
      saveOrder () {
        const orderData = {
          order: this.orderedCategories.map(cat => cat.id),
          useCustomOrder: true // Always use custom order when saving from this modal
        }

        // Emit event to parent component
        this.$bus.emit('categories-reordered', orderData)

        this.close()
      }
    }
  }
</script>

<style scoped>

</style>
